/**
 * 合规页文案。
 *
 * ⚠️ 这些文字是备案审核过的合规资产，逐字来自旧站产物，**不要凭常识改写**。
 * 尤其注意全站措辞刻意避开「预测/运势/改运」，只说「资料整理/学习/记录」——
 * 备案登记的服务类别是「文化、教育、学习资料类网站」，越界即不合规。
 *
 * 对原文的改动（每一处都是因为原文指向了不存在的东西）：
 * 1. 未成年人保护页原提「站内留言、邮箱或客服电话」：电话已于 2026-08-06 从全站移除，
 *    站内留言功能未实现，两者都会指向不存在的渠道，故只保留邮箱。
 * 2. 隐私政策原承诺「可通过数据管理页导出或清除数据」，该页不存在，
 *    改为以邮箱作为行权渠道 —— 个保法要求提供查阅/删除渠道，邮箱人工受理同样合规。
 * 3. 免责声明原提「数据管理入口」，同上，改为「页面公示的联系方式」。
 */
import { site, filing } from "./site";

export type LegalSection = { heading: string; items: string[] };
export type LegalQr = { src: string; caption: string };
export type LegalPage = {
  key: string;
  title: string;
  summary: string;
  body: string[];
  sections: LegalSection[];
  /** 可选二维码，目前用于联系我们页展示企业微信客服 */
  qr?: LegalQr;
};

export const legalPages: LegalPage[] = [
  {
    key: "legal/about",
    title: "关于我们",
    summary:
      "本页用于展示网站主体、服务范围和正式联系方式，是备案审核、用户信任和客服闭环的基础入口。",
    body: [
      "本网站定位为传统文化排盘工具与学习社区，内容用于文化研究、娱乐探索和个人记录管理。",
    ],
    sections: [
      {
        heading: "服务范围",
        items: [
          "八字排盘、合盘、案例记录、学习资料、会员权益和用户留言。",
          "不提供医疗、投资、法律、婚姻结果承诺等现实决策服务。",
        ],
      },
      {
        heading: "经营主体",
        items: [
          `主体名称：${site.company}`,
          `主体类型：${filing.entityType}`,
          `网站域名：${site.domain}`,
        ],
      },
    ],
  },
  {
    key: "legal/terms",
    title: "用户协议",
    summary:
      "用户协议约定服务边界、账号规则、付费规则和禁止行为，正式上线前应由主体负责人确认。",
    body: [
      "用户应确认已年满十八周岁，并承诺不将本工具用于医疗、投资、婚姻、法律等现实决策依据。",
      "平台提供排盘、记录、学习和合盘工具，结果仅供文化娱乐参考，不构成任何专业建议。",
      "用户上传或保存的内容应合法、真实，不得包含违法、侵权、欺诈、违规承诺或诱导消费内容。",
    ],
    sections: [
      {
        heading: "付费与退款",
        items: [
          "会员权益以页面展示为准；未实际开通或重复扣费的订单可申请核验。",
          "数字内容、学习资料和已使用权益的退款规则以上线后的支付协议为准。",
        ],
      },
      {
        heading: "禁止行为",
        items: [
          "不得发布违法违规、违规承诺、诱导转账、侵权或人身攻击内容。",
          "不得以排盘结果替代医疗诊断、投资决策、法律意见或婚恋强制决定。",
        ],
      },
      {
        heading: "服务风险提示",
        items: [
          "传统文化内容存在主观解释差异，平台不承诺任何现实结果。",
          "用户应独立判断，并对自身行为承担责任。",
        ],
      },
    ],
  },
  {
    key: "legal/privacy",
    title: "隐私政策",
    summary: "隐私政策说明个人信息如何收集、使用、保存、导出、删除和保护。",
    body: [
      "当前版本默认使用浏览器本地存储保存账号昵称、案例记录、留言和会员状态；前端同步开关开启后，相关数据会保存到腾讯云服务器后端。",
      "任何涉及手机号、支付、日志、设备信息或地理位置的数据，都应取得用户同意并采取加密与访问控制。",
    ],
    sections: [
      {
        heading: "可能收集的信息",
        items: [
          "账号昵称、手机号或微信标识、排盘资料、案例标签、合盘关系、留言联系方式、订单状态和必要操作日志。",
          "出生日期、时辰、性别、地点等排盘资料属于用户主动填写的信息，应仅用于排盘和记录管理。",
        ],
      },
      {
        heading: "本地存储与同意",
        items: [
          "首次访问会提示本地存储与云端同步用途，用户确认后记录同意版本和时间。",
          "必要本地存储用于保存排盘记录、笔记、留言、订单状态和隐私提示确认状态。",
        ],
      },
      {
        heading: "用户权利",
        items: [
          "用户可通过「联系我们」页面公示的方式申请导出、删除个人数据或注销账号。",
        ],
      },
      {
        heading: "安全措施",
        items: ["后台管理接口需要管理员令牌，云端操作写入审计日志。"],
      },
    ],
  },
  {
    key: "legal/minors",
    title: "未成年人保护",
    summary:
      "平台面向成年人提供传统文化工具，未成年人不应注册、付费或接受个性化解读服务。",
    body: [
      "平台拒绝向未成年人提供付费服务和个性化资料解读服务。",
      "如监护人发现未成年人使用本产品，可通过邮箱联系平台处理。",
    ],
    sections: [
      {
        heading: "使用限制",
        items: [
          "注册、会员、付费、个性化咨询和案例保存服务仅面向已满十八周岁的用户。",
          "发现疑似未成年人账号时，平台可暂停相关权益并要求补充监护人确认。",
        ],
      },
      {
        heading: "监护人处理",
        items: [
          `监护人可通过 ${site.email} 联系平台核验和删除相关数据。`,
          "涉及付费订单时，平台将按支付记录、权益使用情况和监管要求处理。",
        ],
      },
    ],
  },
  {
    key: "legal/disclaimer",
    title: "风险提示与免责声明",
    summary:
      "说明本站传统文化排盘内容的服务边界、禁止用途和用户自担风险，是排盘前确认与投诉处理的依据。",
    body: [
      "本站提供的八字排盘、合盘、专业细盘、学习资料和案例记录，仅用于传统文化学习、娱乐参考和个人记录管理。",
      "本站不提供医疗诊断、投资建议、法律意见、婚姻结果承诺、就业升学决定或其他现实决策服务。",
      "用户不得利用本站内容进行违规承诺、诱导消费、恐吓他人、歧视攻击、违法营销或侵犯他人隐私。",
    ],
    sections: [
      {
        heading: "禁止替代现实决策",
        items: [
          "任何排盘结果都不应替代医生、律师、财务顾问、心理咨询师等专业人士意见。",
          "涉及疾病、投资、借贷、婚育、升学、就业等事项时，应以现实证据和专业意见为准。",
        ],
      },
      {
        heading: "个人信息提醒",
        items: [
          "出生日期、时辰、地点、姓名和关系信息可能涉及个人隐私，用户应确保有权填写和保存。",
          "请勿上传他人身份证号、住址、联系方式、支付凭证等不必要敏感信息。",
        ],
      },
      {
        heading: "平台处理权",
        items: [
          "发现诱导转账、结果承诺、冒充客服、违法违规或侵犯未成年人权益的内容时，平台可删除、限制、封禁并保留审计记录。",
          "用户可通过页面公示的联系方式申请处理。",
        ],
      },
    ],
  },
  {
    key: "legal/contact",
    title: "联系我们",
    summary: "用于承接客服、投诉、备案核验、数据权利和商务合作。",
    body: [
      `主体名称：${site.company}`,
      `联系邮箱：${site.email}`,
      `网站域名：${site.domain}`,
    ],
    sections: [
      {
        heading: "处理范围",
        items: ["账号、订单、数据导出/删除、内容投诉、未成年人保护、商务合作。"],
      },
    ],
    qr: { src: "/wecom-qr.png", caption: "企业微信客服 · 扫码添加" },
  },
];

export function findLegalPage(key: string): LegalPage | undefined {
  return legalPages.find((page) => page.key === key);
}
